import { Button, ButtonProps } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Accept, useDropzone } from 'react-dropzone';

interface FileDropzoneProps {
    file?: string;
    onFileChange: (file: File | null) => void;
    accept?: Accept;
    multiple?: boolean;
    noClick?: boolean;
    noDrag?: boolean;
    disabled?: boolean;
    selectButtonLabel?: string;
    removeButtonLabel?: string;
    showRemoveButton?: boolean;
    renderContent?: (selectedFile: File | null) => React.ReactNode;
    containerStyle?: React.CSSProperties;
    actionContainerStyle?: React.CSSProperties;
    selectButtonProps?: Omit<ButtonProps, 'type' | 'onClick'>;
    removeButtonProps?: Omit<ButtonProps, 'type' | 'onClick'>;
}

const FileDropzone: React.FC<FileDropzoneProps> = ({
    file,
    onFileChange,
    accept,
    multiple = false,
    noClick = true,
    noDrag = true,
    disabled = false,
    selectButtonLabel = 'Select File',
    removeButtonLabel = 'Remove File',
    showRemoveButton = true,
    renderContent,
    containerStyle,
    actionContainerStyle,
    selectButtonProps,
    removeButtonProps,
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (!acceptedFiles.length) {
            return;
        }

        setSelectedFile(acceptedFiles[0]);
    }, []);

    const { getRootProps, getInputProps, open } = useDropzone({
        multiple,
        noClick,
        noDrag,
        disabled,
        accept,
        onDrop,
    });

    useEffect(() => {
        onFileChange(selectedFile);
    }, [selectedFile, onFileChange]);

    const onRemoveFile = () => {
        setSelectedFile(null);
    };

    const hasFile = Boolean(selectedFile || file);

    return (
        <div style={containerStyle}>
            {renderContent?.(selectedFile)}
            <div {...getRootProps()} style={actionContainerStyle}>
                <input {...getInputProps()} />
                <Button type="button" variant="contained" onClick={open} disabled={disabled} {...selectButtonProps}>
                    {selectButtonLabel}
                </Button>
                {showRemoveButton && (
                    <Button
                        type="button"
                        variant="contained"
                        color="error"
                        onClick={onRemoveFile}
                        disabled={disabled || !hasFile}
                        {...removeButtonProps}
                    >
                        {removeButtonLabel}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default FileDropzone;